import * as React from "react";
import {
  useMotionTemplate,
  motion,
  useMotionValue,
  MotionValue,
} from "framer-motion";

import { cn } from "@/lib/cn";
import { generateRandomString } from "@/utils";

type EvervaultCardProps = {
  bgColor?: string;
  className?: string;
  children: React.ReactNode;
};

/**
 * Renders an Evervault card with a radial gradient pattern
 *
 * @param bgColor - The background color of the card
 * @param className - Additional CSS classes
 * @param children - The content to be displayed inside the card
 */
const EvervaultCard: React.FC<EvervaultCardProps> = ({
  bgColor,
  className,
  children,
}) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const [randomString, setRandomString] = React.useState("");

  React.useEffect(() => {
    setRandomString(generateRandomString(1500));
  }, []);

  const onMouseMove = React.useCallback(
    ({
      currentTarget,
      clientX,
      clientY,
    }: {
      currentTarget: HTMLDivElement;
      clientX: number;
      clientY: number;
    }) => {
      const { left, top } = currentTarget.getBoundingClientRect();
      mouseX.set(clientX - left);
      mouseY.set(clientY - top);
      setRandomString(generateRandomString(1500));
    },
    [mouseX, mouseY]
  );

  return (
    <div
      className={cn(
        "pointer-events-none relative flex aspect-square h-full w-full items-center justify-center bg-transparent md:pointer-events-auto",
        className
      )}
    >
      <div
        onMouseMove={onMouseMove}
        className="group/card relative flex h-full w-full items-center justify-center overflow-hidden bg-transparent p-0"
      >
        <CardPattern
          mouseX={mouseX}
          mouseY={mouseY}
          randomString={randomString}
          bgColor={bgColor}
        />
        <div className="absolute inset-0 h-[120%] w-[120%] bg-black/50 blur-sm group-hover:bg-black/50 md:bg-transparent md:group-hover:!bg-black/[0.6]" />
        <div className="relative z-10">{children}</div>
      </div>
    </div>
  );
};

export default EvervaultCard;

type CardPatternProps = {
  mouseX: MotionValue<number>;
  mouseY: MotionValue<number>;
  randomString: string;
  bgColor: string | undefined;
};

/**
 * Renders a card pattern with a radial gradient
 *
 * @param mouseX - The x-coordinate of the mouse
 * @param mouseY - The y-coordinate of the mouse
 * @param randomString - A random string
 * @param bgColor - The background color of the card
 */
const CardPattern: React.FC<CardPatternProps> = React.memo(
  ({ mouseX, mouseY, randomString, bgColor }) => {
    const maskImage = useMotionTemplate`radial-gradient(250px at ${mouseX}px ${mouseY}px, white, transparent)`;
    const style = { maskImage, WebkitMaskImage: maskImage };

    return (
      <div className="pointer-events-none">
        <div className="absolute inset-0 [mask-image:linear-gradient(white,transparent)] group-hover/card:opacity-50" />
        <motion.div
          className="absolute inset-0 opacity-100 backdrop-blur-xl transition duration-500 md:opacity-0 md:group-hover/card:opacity-100"
          style={{ backgroundColor: bgColor, ...style }}
        />
        <motion.div
          className="absolute inset-0 opacity-100 mix-blend-overlay group-hover/card:opacity-100 md:opacity-0"
          style={style}
        >
          <p className="text-primary-200 absolute inset-x-0 h-full whitespace-pre-wrap break-words font-mono text-xs font-bold transition duration-500">
            {randomString}
          </p>
        </motion.div>
      </div>
    );
  }
);

CardPattern.displayName = "CardPattern";
